import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtErr, apiPost, TOKEN_KEY } from "@/lib/shieldx";
import { Wordmark } from "@/components/shieldx/Logo";
import { Loader2, ArrowRight, ShieldCheck, BadgeCheck, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/shieldx/PasswordInput";

export default function Login() {
  const { login, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [secureStep, setSecureStep] = useState(false);
  const [privOpen, setPrivOpen] = useState(false);
  const [privEmail, setPrivEmail] = useState("");
  const [privPassword, setPrivPassword] = useState("");
  const [privErr, setPrivErr] = useState("");
  const [privLoading, setPrivLoading] = useState(false);

  const routeFor = (u) => {
    if (u?.role === "super_admin") {
      setSecureStep(true);
      setTimeout(() => navigate("/app/super-admin"), 1400);
    } else {
      navigate("/app/command-center");
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success("Signed in to ShieldX");
      routeFor(u);
    } catch (ex) {
      setErr(fmtErr(ex?.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  // Privileged access: identical response for wrong credentials and non-privileged
  // accounts — never reveals whether an account holds Super Admin rights.
  const privSubmit = async (e) => {
    e?.preventDefault();
    setPrivLoading(true); setPrivErr("");
    try {
      const u = await login(privEmail, privPassword);
      if (u?.role === "super_admin") {
        setPrivOpen(false); setSecureStep(true);
        setTimeout(() => navigate("/app/super-admin"), 1400);
      } else {
        try { await logout(); } catch {}
        setPrivErr("ACCESS RESTRICTED");
      }
    } catch {
      setPrivErr("ACCESS RESTRICTED");
    } finally { setPrivLoading(false); }
  };

  const google = () => {};

  return (
    <div className="relative grid min-h-screen bg-slate-950 lg:grid-cols-[1.05fr_1fr]">
      {secureStep && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/95 backdrop-blur-sm" data-testid="secure-admin-access">
          <div className="w-full max-w-sm rounded-2xl border border-amber-500/30 bg-slate-900 p-8 text-center shadow-2xl">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300"><ShieldCheck className="h-6 w-6" /></span>
            <h2 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-50">Secure Admin Access</h2>
            <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-emerald-400"><BadgeCheck className="h-4 w-4" /> Identity verified</p>
            <p className="mt-1 text-xs text-slate-400">Additional authorization active · establishing privileged session…</p>
            <p className="mt-4 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Routing to Command Center</p>
          </div>
        </div>
      )}
      {/* Left — institutional brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden border-r border-slate-800/80 bg-slate-900/30 p-14 lg:flex">
        <div className="absolute inset-0 sx-grid opacity-60" />
        <div className="relative"><Wordmark markSize={30} /></div>
        <div className="relative max-w-lg">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">ShieldX</p>
          <h2 className="mt-4 font-display text-4xl font-bold leading-[1.1] tracking-tight text-slate-50">
            The Digital Shield for the Autonomous Era
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
            A trusted control layer for protecting digital assets from unauthorized autonomous activity.
            Authorization is evaluated at the boundary — before access is granted.
          </p>
          <div className="mt-8 h-px w-16 bg-slate-700" />
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Intelligence can evolve. Authority must remain controlled.</p>
        </div>
        <p className="relative flex items-center font-mono text-[11px] text-slate-600">
          Zero-Trust · Least Privilege · Tamper-Evident Audit
          <button type="button" onClick={() => { setPrivErr(""); setPrivOpen(true); }}
            aria-label="Privileged access" data-testid="privileged-access-dot"
            className="group/dot relative ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full align-middle outline-none focus-visible:ring-1 focus-visible:ring-amber-400/40">
            <span className="h-1 w-1 rounded-full bg-slate-600 transition-all duration-300 group-hover/dot:bg-amber-400/80 group-hover/dot:shadow-[0_0_6px_1px] group-hover/dot:shadow-amber-400/40 group-focus-visible/dot:bg-amber-400" />
            <span className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-700 bg-slate-800/95 px-2 py-1 text-[10px] text-slate-300 opacity-0 transition-opacity duration-200 group-hover/dot:opacity-100 group-focus-visible/dot:opacity-100">
              Privileged access
            </span>
          </button>
        </p>
      </div>

      {/* Right — sign in */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="relative w-full max-w-[380px]">
          <div className="mb-10 lg:hidden"><Wordmark markSize={28} /></div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-50">Welcome back</h1>
          <p className="mt-1.5 text-sm text-slate-400">Manage your protected assets and authorization controls.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-2 block text-xs font-medium tracking-wide text-slate-400">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 w-full rounded-lg border border-slate-800 bg-slate-900/60 px-3.5 text-sm text-slate-100 outline-none transition-colors focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/15" />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="block text-xs font-medium tracking-wide text-slate-400">Password</label>
                <button type="button" onClick={() => toast.info("Password reset link would be sent to your email.")} className="text-xs text-slate-500 hover:text-slate-300">Forgot password?</button>
              </div>
              <PasswordInput data-testid="login-password" testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                className="h-11 w-full rounded-lg border border-slate-800 bg-slate-900/60 px-3.5 text-sm text-slate-100 outline-none transition-colors focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/15" />
            </div>
            {err && <p data-testid="login-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">{err}</p>}
            <button data-testid="login-submit" disabled={loading} type="submit"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500">
            New to ShieldX? <Link to="/register" className="font-medium text-blue-400 hover:text-blue-300">Create an organization</Link>
          </p>
          <p className="mt-10 text-center font-mono text-[11px] tracking-wide text-slate-600">Powered by Vijay Software Solutions Pvt Ltd</p>
        </div>
      </div>

      {/* Privileged Access authentication panel */}
      {privOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="privileged-access-panel">
          <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-sm" onClick={() => setPrivOpen(false)} />
          <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-amber-500/30 bg-slate-900 shadow-2xl">
            <button onClick={() => setPrivOpen(false)} className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200" aria-label="Close"><X className="h-4 w-4" /></button>
            <div className="border-b border-slate-800 bg-amber-500/5 px-6 py-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300"><KeyRound className="h-5 w-5" /></span>
              <h2 className="mt-3 font-display text-base font-bold tracking-tight text-slate-50">Privileged Access</h2>
              <p className="mt-0.5 text-xs text-slate-400">Additional authorization is required. Authentication is verified server-side.</p>
            </div>
            <form onSubmit={privSubmit} className="space-y-3.5 p-6">
              <div>
                <label className="mb-1.5 block text-xs font-medium tracking-wide text-slate-400">Email</label>
                <input data-testid="priv-email" type="email" required value={privEmail} onChange={(e) => setPrivEmail(e.target.value)}
                  className="h-11 w-full rounded-lg border border-slate-800 bg-slate-950 px-3.5 text-sm text-slate-100 outline-none focus:border-amber-500/50" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium tracking-wide text-slate-400">Password</label>
                <PasswordInput data-testid="priv-password" testid="priv-password" required value={privPassword} onChange={(e) => setPrivPassword(e.target.value)} autoComplete="current-password"
                  className="h-11 w-full rounded-lg border border-slate-800 bg-slate-950 px-3.5 text-sm text-slate-100 outline-none focus:border-amber-500/50" />
              </div>
              {privErr && <p data-testid="priv-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold tracking-wide text-red-300">{privErr}</p>}
              <button data-testid="priv-submit" disabled={privLoading} type="submit"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-500 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
                {privLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Authorize <ArrowRight className="h-4 w-4" /></>}
              </button>
              <p className="text-center text-[10px] text-slate-600">ShieldX verifies identity and role server-side. Access is granted only to authorized administrators.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
